import { useState, useRef, useEffect, useMemo } from 'react';
import {
  createPromotion,
  getProductAndOfferList,
  getPromotionData,
  updatePromotion,
  generatePreview,
  getErrorMessages,
  updatedPayload,
} from '../service/CreatePromotionService';
import { DISCOUNT, FINANCE, SWEETENER } from '../constants';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';

/**
 * Custom hook for managing promotion creation and editing functionality
 * @param {Object} config Configuration object
 */

export const useCreatePromotionHook = (config) => {
  const {
    id,
    setStatus,
    setIsDiscountClickable,
    setIsFinancialClickable,
    setSelectedStores,
    setSelectedConjunction,
    setCanShowFlipButton,
    setFlipOffer,
    setDisableDRAFTBtn,
    setDisableUPDATEBtn,
    mode,
    setSelectedStartDate,
    setSelectedEndDate,
    setHistoricPromotionData,
    historicPromotionData,
    setShowSnackBar,
    setSnackBarSeverity,
    setSnackBarMessage,
    setIsNotesClickable,
  } = config;
  const apiCalledOnFirstLoad = useRef(false);
  const fetchHeadlineApiCalledOnFirstLoad = useRef(false);
  const [promotionData, setPromotionData] = useState({});
  const [productsList, setProductsList] = useState([]);
  const [discountOffersList, setDiscountOffersList] = useState([]);
  const [financeOffersList, setFinanceOffersList] = useState([]);
  const [sweetnerOffersList, setSweetnerOffersList] = useState([]);
  const [offerApplicable, setOfferApplicable] = useState(false);
  const [requiredValue, setRequiredValue] = useState(null);
  const [isRequiredChecked, setIsRequiredChecked] = useState(false);
  const [excludedProducts, setExcludedProducts] = useState([]);
  const [customHeadline, setCustomHeadline] = useState('');
  const [customDisclaimer, setCustomDisclaimer] = useState('');
  const [minRequiredTextFieldValue, setMinRequiredTextFieldValue] = useState('');
  const [selectHeadline, setSelectHeadline] = useState('none');
  const [previewData, setPreviewData] = useState(null);
  const editPromotionCheck = useRef(mode !== 'Edit');
  const [loadedPromotionData, setLoadedPromotionData] = useState(false);
  const navigate = useNavigate();
  const [showBtnLoader, setShowBtnLoader] = useState(false);
  const [showDraftBtnLoader, setShowDraftBtnLoader] = useState(false);
  const [showUpdateBtnLoader, setShowUpdateBtnLoader] = useState(false);
  const [dataLoader, setDataLoader] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [headlineLoading, setHeadlineLoading] = useState(false);

  /**
   *
   * @param func
   * @param wait
   */
  function debounce(func, wait) {
    let timeout;

    const debouncedFunc = function (...args) {
      const context = this;

      const later = () => {
        timeout = null;
        func.apply(context, args);
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };

    // Adding a `cancel` method to cancel the debounced call
    debouncedFunc.cancel = function () {
      clearTimeout(timeout);
      timeout = null;
    };

    return debouncedFunc;
  }

  const showSnackBar = (severity, message) => {
    setSnackBarSeverity(severity);
    setSnackBarMessage(message);
    setShowSnackBar(true);
  };

  async function handleCreatePromotion() {
    try {
      setShowBtnLoader(true);
      const response = await createPromotion(promotionData);

      if (response?.data?.success) {
        showSnackBar('success', 'Promotion created successfully');
        setTimeout(navigateToPromotions, 2000);
      } else if (response?.success === false) {
        setShowBtnLoader(false);
        showSnackBar('error', getErrorMessages(response));
      } else {
        setShowBtnLoader(false);
        showSnackBar('error', 'Error creating promotion');
      }
    } catch (ex) {
      setShowBtnLoader(false);
      showSnackBar('error', 'Error creating promotion');
      console.error(ex);
    }
  }

  const handleSavePromotion = async () => {
    try {
      setShowUpdateBtnLoader(true);
      let payload = {
        ...promotionData,
        ...(promotionData?.status === 'PUBLISHED' && { notify_email: notifyEmail }),
      };

      payload = removeNullValuesFromPayload(payload);
      const response = await updatePromotion(payload);

      if (response?.data?.success) {
        showSnackBar('success', 'Promotion saved successfully');
        setTimeout(navigateToPromotions, 2000);
      } else if (response?.success === false) {
        setShowUpdateBtnLoader(false);
        showSnackBar('error', getErrorMessages(response));
      } else {
        setShowUpdateBtnLoader(false);
        showSnackBar('error', 'Error saving promotion');
      }
    } catch (ex) {
      setShowUpdateBtnLoader(false);
      showSnackBar('error', 'Error saving promotion');
      console.error(ex);
    }
  };

  const removeNullValuesFromPayload = (payload) => {
    Object.keys(payload).forEach((key) => payload[key] === null && delete payload[key]);
    return payload;
  };

  const handlePublishPromotion = async () => {
    try {
      setShowBtnLoader(true);
      let payload = { ...promotionData, ...{ notify_email: notifyEmail } };

      payload = removeNullValuesFromPayload(payload);
      payload.status = 'PUBLISHED';
      const response = await updatePromotion(payload);

      if (response?.data?.success) {
        showSnackBar('success', 'Promotion published successfully');
        setTimeout(navigateToPromotions, 2000);
      } else if (response?.success === false) {
        setShowBtnLoader(false);
        showSnackBar('error', getErrorMessages(response));
      } else {
        setShowBtnLoader(false);
        showSnackBar('error', 'Error publishing promotion');
      }
    } catch (ex) {
      setShowBtnLoader(false);
      showSnackBar('error', 'Error publishing promotion');
      console.error(ex);
    }
  };

  const handleDraftPromotion = async () => {
    try {
      setShowDraftBtnLoader(true);
      let payload = { ...promotionData };

      payload.status = 'DRAFT';
      payload = removeNullValuesFromPayload(payload);
      setPromotionData({ ...promotionData, status: 'DRAFT' });
      const response = await updatePromotion(payload);

      if (response?.data?.success) {
        showSnackBar('success', 'Promotion saved as draft successfully');
        setTimeout(navigateToPromotions, 2000);
      } else if (response?.success === false) {
        setShowDraftBtnLoader(false);
        showSnackBar('error', getErrorMessages(response));
      } else {
        setShowDraftBtnLoader(false);
        showSnackBar('error', 'Error saving promotion as draft');
      }
    } catch (ex) {
      setShowDraftBtnLoader(false);
      showSnackBar('error', 'Error saving promotion as draft');
      console.error(ex);
    }
  };

  const fetchHeadlineData = async (promotionData) => {
    try {
      setHeadlineLoading(true);
      const payload = { ...updatedPayload(promotionData, financeOffersList) };
      const response = await generatePreview(payload);

      if (response?.success === false) {
        setDisableUPDATEBtn(true);
        setDisableDRAFTBtn(true);
        showSnackBar('error', getErrorMessages(response));
      }
      if (response !== undefined && response.data) {
        setPreviewData(response.data.data);
      }
      if (!editPromotionCheck.current) {
        editPromotionCheck.current = true;
      }
    } catch (ex) {
      console.error(ex);
    } finally {
      setHeadlineLoading(false);
    }
  };
  const navigateToPromotions = () => {
    setShowUpdateBtnLoader(false);
    setShowDraftBtnLoader(false);
    setShowBtnLoader(false);
    navigate(ROUTES.promotions);
  };

  const handleNotifyEmail = (event) => {
    setNotifyEmail(event.target.checked);
  };

  useEffect(() => {
    try {
      if (!apiCalledOnFirstLoad.current) {
        setDataLoader(true);
        apiCalledOnFirstLoad.current = true;
        getProductAndOfferList().then(([products, offers]) => {
          setProductsList(products.data);
          const offerList = offers?.data;

          if (offerList?.length) {
            const discountOffers = offerList.filter((item) => item.type === DISCOUNT);
            const financeOffers = offerList.filter((item) => item.type === FINANCE);
            const sweetnerOffers = offerList.filter((item) => item.type === SWEETENER);

            setDiscountOffersList(discountOffers);
            setFinanceOffersList(financeOffers);
            setSweetnerOffersList(sweetnerOffers);
            setDataLoader(false);
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  /**
   * @param id
   */
  async function fetchPromotionData(id) {
    try {
      const response = await getPromotionData(id);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          const data = await fetchPromotionData(id);
          const updatedData = { ...data.data };

          if (updatedData.store_id !== undefined) {
            updatedData.store_ids = [updatedData.store_id];
          }
          setSelectedConjunction(data?.data?.conjunction || '');
          setStatus(data?.data?.status);
          setSelectedStores([data?.data?.store_id]);
          setFlipOffer(data?.data?.flip);
          setSelectedStartDate(data?.data?.start_date);
          setSelectedEndDate(data?.data?.end_date);
          setHistoricPromotionData(JSON.parse(JSON.stringify(updatedData)));
          setLoadedPromotionData(true);
          if (data?.data?.store_id !== undefined) {
            setIsDiscountClickable(true);
            setIsFinancialClickable(true);
            setIsNotesClickable(true);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [id]);

  const fetchHeadlineDataRef = useRef(fetchHeadlineData);
  useEffect(() => {
    fetchHeadlineDataRef.current = fetchHeadlineData;
  });

  const debouncedFetchHeadlineData = useMemo(
    () => debounce((promotionData) => fetchHeadlineDataRef.current(promotionData), 500),
    []
  );

  // Helper function to check if flip button should be shown
  const shouldShowFlipButton = (promotionData) => {
    const hasDiscountAndOneFinance =
      promotionData?.discount_offer !== undefined &&
      promotionData?.discount_offer !== null &&
      promotionData?.finance_offer !== undefined &&
      promotionData?.finance_offer !== null &&
      promotionData?.finance_offer.length === 1;

    const hasTwoFinanceOffers =
      promotionData?.finance_offer !== undefined &&
      promotionData?.finance_offer !== null &&
      promotionData?.finance_offer.length === 2;

    return hasDiscountAndOneFinance || hasTwoFinanceOffers;
  };

  // Helper function to handle headline data fetching
  const handleHeadlineDataFetch = (promotionData) => {
    if (promotionData?.store_ids === undefined) {
      return;
    }

    if (!fetchHeadlineApiCalledOnFirstLoad.current) {
      fetchHeadlineApiCalledOnFirstLoad.current = true;
      return;
    }

    if (!headlineLoading) {
      debouncedFetchHeadlineData(promotionData);
    }
  };

  // Stable JSON stringify that sorts object keys recursively.
  // This ensures the same output regardless of key insertion order,
  // which differs between API responses (historicPromotionData) and
  // locally constructed state (promotionData) in production builds.
  const stableStringify = (value) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) {
      return '[' + value.map((item) => stableStringify(item)).join(',') + ']';
    }
    const sortedKeys = Object.keys(value).sort((a, b) => a.localeCompare(b));
    const parts = sortedKeys.map((key) => JSON.stringify(key) + ':' + stableStringify(value[key]));
    return '{' + parts.join(',') + '}';
  };

  // Normalize an offer value for dirty comparison.
  // Treats undefined, null, and empty objects ({}) as equivalent (null)
  // so that checking/unchecking an "include" checkbox without entering
  // data does not count as a real change.
  const normalizeOffer = (offer) => {
    if (offer === undefined || offer === null) return null;
    if (Array.isArray(offer)) {
      const filtered = offer.filter((o) => o && Object.keys(o).length > 0);
      return filtered.length > 0 ? filtered : null;
    }
    if (typeof offer === 'object' && Object.keys(offer).length === 0) return null;
    return offer;
  };

  // Normalize a scalar value for dirty comparison.
  // Treats undefined, null, and empty string as equivalent (null)
  // so that toggling include checkboxes (e.g. notes) that set '' on
  // check/uncheck does not count as a real change from the original.
  const normalizeValue = (val) => {
    if (val === undefined || val === null || val === '') return null;
    return val;
  };

  // Helper function to extract comparable promotion data for dirty check
  // Uses stableStringify instead of JSON.stringify to ensure key-order
  // independent comparison across dev and production builds.
  const getComparableData = (data) => {
    if (!data) return null;
    return stableStringify({
      discount_offer: normalizeOffer(data.discount_offer),
      finance_offer: normalizeOffer(data.finance_offer),
      sweetener_offer: normalizeOffer(data.sweetener_offer),
      conjunction: normalizeValue(data.conjunction),
      flip: data.flip ?? null,
      start_date: normalizeValue(data.start_date),
      end_date: normalizeValue(data.end_date),
      freeze_date: normalizeValue(data.freeze_date),
      notes: normalizeValue(data.notes),
      store_ids: data.store_ids ?? null,
    });
  };

  // Helper function to enable/disable update/draft buttons in edit mode
  // Only enables when promotion data has actually changed from the original
  const enableEditModeButtons = (promotionData) => {
    if (!editPromotionCheck.current || mode !== 'Edit' || promotionData?.store_ids === undefined) {
      return;
    }

    const hasChanged =
      getComparableData(promotionData) !== getComparableData(historicPromotionData);

    setDisableUPDATEBtn(!hasChanged);
    setDisableDRAFTBtn(!hasChanged);
  };

  useEffect(() => {
    setCanShowFlipButton(shouldShowFlipButton(promotionData));
    handleHeadlineDataFetch(promotionData);
    enableEditModeButtons(promotionData);

    return () => {
      debouncedFetchHeadlineData.cancel();
      fetchHeadlineApiCalledOnFirstLoad.current = true;
    };
  }, [promotionData]);

  return {
    promotionData,
    setPromotionData,
    productsList,
    discountOffersList,
    financeOffersList,
    sweetnerOffersList,
    offerApplicable,
    setOfferApplicable,
    requiredValue,
    setRequiredValue,
    isRequiredChecked,
    setIsRequiredChecked,
    excludedProducts,
    setExcludedProducts,
    customHeadline,
    setCustomHeadline,
    customDisclaimer,
    setCustomDisclaimer,
    minRequiredTextFieldValue,
    setMinRequiredTextFieldValue,
    selectHeadline,
    setSelectHeadline,
    handleCreatePromotion,
    handleSavePromotion,
    handlePublishPromotion,
    handleDraftPromotion,
    previewData,
    loadedPromotionData,
    showBtnLoader,
    dataLoader,
    fetchHeadlineData,
    handleNotifyEmail,
    notifyEmail,
    showDraftBtnLoader,
    showUpdateBtnLoader,
    headlineLoading,
  };
};
